/**
 * Lightweight typed fetch wrappers for the Chat IA REST endpoints.
 * We hand-roll these (vs. orval codegen) because the chat surface is
 * still iterating quickly and we already use direct fetch for the
 * SSE streaming path.
 */

import { getImpersonationHeaders } from "./impersonation";

export interface ChatWorkspace {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatConversation {
  id: string;
  workspaceId: string | null;
  title: string;
  lastMessageAt: string;
  createdAt: string;
  /**
   * Single-line preview of the most recent message in the conversation
   * (any role), trimmed and truncated server-side. `null` when the
   * conversation has no messages yet (e.g. just-created). Only the list
   * endpoint populates this; per-conversation fetches omit it.
   */
  lastMessagePreview?: string | null;
}

export interface ChatMessage {
  id: string;
  role: string;
  content: string;
  meta: Record<string, unknown> | null;
  createdAt: string;
}

export interface ChatFile {
  id: string;
  workspaceId: string;
  name: string;
  mime: string;
  sizeBytes: number;
  status: "processing" | "ready" | "failed";
  chunkCount: number;
  errorMsg: string | null;
  createdAt: string;
}

async function jsonFetch<T>(
  url: string,
  init?: RequestInit,
): Promise<T> {
  const r = await fetch(url, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...getImpersonationHeaders(),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try {
      const body = await r.json();
      if (typeof body?.error === "string") msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return (await r.json()) as T;
}

// ---------- workspaces ----------

export const listChatWorkspaces = () =>
  jsonFetch<{ workspaces: ChatWorkspace[] }>("/api/chat/workspaces");

export const createChatWorkspace = (name: string) =>
  jsonFetch<{ workspace: ChatWorkspace }>("/api/chat/workspaces", {
    method: "POST",
    body: JSON.stringify({ name }),
  });

export const deleteChatWorkspace = (id: string) =>
  jsonFetch<{ ok: boolean }>(`/api/chat/workspaces/${id}`, {
    method: "DELETE",
  });

// ---------- files ----------

export const listChatFiles = (workspaceId: string) =>
  jsonFetch<{ files: ChatFile[] }>(
    `/api/chat/workspaces/${workspaceId}/files`,
  );

export async function uploadChatFile(
  workspaceId: string,
  file: File,
): Promise<ChatFile> {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`/api/chat/workspaces/${workspaceId}/files`, {
    method: "POST",
    body: fd,
    credentials: "same-origin",
    headers: { ...getImpersonationHeaders() },
  });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try {
      const body = await r.json();
      if (typeof body?.error === "string") msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const body = (await r.json()) as { file: ChatFile };
  return body.file;
}

export const deleteChatFile = (id: string) =>
  jsonFetch<{ ok: boolean }>(`/api/chat/files/${id}`, { method: "DELETE" });

// ---------- conversations ----------

export const listChatConversations = () =>
  jsonFetch<{ conversations: ChatConversation[] }>("/api/chat/conversations");

export const createChatConversation = (input: {
  workspaceId?: string | null;
  title?: string;
}) =>
  jsonFetch<{ conversation: ChatConversation }>("/api/chat/conversations", {
    method: "POST",
    body: JSON.stringify(input),
  });

export const getChatConversation = (id: string) =>
  jsonFetch<{
    conversation: ChatConversation;
    messages: ChatMessage[];
  }>(`/api/chat/conversations/${id}`);

export const deleteChatConversation = (id: string) =>
  jsonFetch<{ ok: boolean }>(`/api/chat/conversations/${id}`, {
    method: "DELETE",
  });

export const patchChatConversation = (
  id: string,
  patch: { title?: string; workspaceId?: string | null },
) =>
  jsonFetch<{ conversation: ChatConversation }>(
    `/api/chat/conversations/${id}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
